import { customThemeValid, defaultSectionTitles, normalizeHex } from '../data/templates'
import { ctaById } from '../data/ctas'
import { fontById } from '../data/fonts'
import { stripUndefined } from './objects'
import type {
  AvatarShape, AvatarSize, BadgeIcon, BadgePosition, BannerConfig, BannerRatio, BannerLayout, BorderColorChoice, BorderStyle, BorderWidth, ButtonLook, ButtonRadius,
  ButtonSize, CardStyle, ColorThemeId, ContrastMode, CustomTheme, FeaturedStyle, FontScale, FontTheme, FrameStyle, IconShape,
  CtaCategory, CtaConfig, LinkButtonStyle, PageWidth, PressFeedback, ProfileDesignId, ProfileMusic, ProfileSection, PublicProfile, SavedPalette,
  SectionDisplay, SectionGap, SectionType, SocialStyle, ContactStyle, SurfaceOpacity, TitleAlign, TitleCase,
} from '../types/models'

const sectionTypes: SectionType[] = ['links', 'social', 'projects', 'videos', 'contact', 'banners', 'cta']
const coreSections: SectionType[] = ['links', 'social', 'projects', 'videos', 'contact']
const profileDesignIds: ProfileDesignId[] = ['centered', 'row']
const colorIds: ColorThemeId[] = ['emerald-night', 'paper-pine', 'cobalt-signal', 'neutral-graphite', 'pure-monochrome', 'plum-ember', 'ocean-current', 'sandstone', 'cherry-cream', 'graphite-lime', 'custom']
const borderList: BorderStyle[] = ['none', 'solid', 'dashed', 'dotted']
const frameList: FrameStyle[] = ['none', 'line', 'dashed', 'dotted', 'double', 'inner', 'gradient', 'stitch']
const borderColorList: BorderColorChoice[] = ['auto', 'accent', 'text', 'custom']
const buttonLookList: ButtonLook[] = ['surface', 'soft', 'accent', 'outline']
const buttonSizeList: ButtonSize[] = ['slim', 'normal', 'large']
const iconShapeList: IconShape[] = ['circle', 'squircle', 'square']
const featuredList: FeaturedStyle[] = ['fill', 'outline']
const pressList: PressFeedback[] = ['none', 'scale']
const surfaceOpacityList: SurfaceOpacity[] = ['low', 'medium', 'high']
const fontScaleList: FontScale[] = ['compact', 'normal', 'large']
const titleCaseList: TitleCase[] = ['uppercase', 'normal']
const titleAlignList: TitleAlign[] = ['left', 'center']
const pageWidthList: PageWidth[] = ['narrow', 'normal', 'wide']
const sectionGapList: SectionGap[] = ['compact', 'normal', 'relaxed']
const badgeIconList: BadgeIcon[] = ['check', 'crown', 'gem', 'rocket', 'sparkles', 'flame', 'bolt', 'trophy', 'medal', 'shield', 'heart']
const widthList: BorderWidth[] = ['thin', 'regular', 'bold']
const radiusList: ButtonRadius[] = ['square', 'soft', 'rounded', 'extra', 'pill']
const shapeList: AvatarShape[] = ['circle', 'squircle', 'rounded', 'square']
const sizeList: AvatarSize[] = ['s', 'm', 'l']
const badgeList: BadgePosition[] = ['name', 'avatar', 'hidden']
const contrastList: ContrastMode[] = ['light-content', 'dark-content', 'strong']
const displayList: SectionDisplay[] = ['swipe', 'carousel', 'grid', 'expand']
const cardList: CardStyle[] = ['plain', 'outline', 'cover', 'compact']
const linkButtonList: LinkButtonStyle[] = ['full', 'grid', 'logos', 'detailed']
const socialList: SocialStyle[] = ['logos', 'grid', 'compact']
const contactList: ContactStyle[] = ['icons', 'buttons', 'grid']

/** Old v4 and earlier design ids mapped onto the v5 system. */
const oldProfileDesigns: Record<string, ProfileDesignId> = {
  'centered-identity': 'centered', 'compact-row': 'row', 'cover-overlap': 'centered', 'editorial-name-first': 'row', 'creator-spotlight': 'centered',
  primary: 'centered', split: 'row', compact: 'row',
}
const oldSocialStyles: Record<string, SocialStyle> = { grid: 'logos', compact: 'compact', cards: 'grid' }
const oldBorderStyles: Record<string, BorderStyle> = { layout: 'none' }
const oldFrameStyles: Record<string, FrameStyle> = { solid: 'line', layout: 'none', line: 'line' }

const clamp = (value: unknown, fallback: number) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : fallback
function pick<T extends string>(value: unknown, allowed: T[], fallback: T, legacy?: Record<string, T>): T {
  if (allowed.includes(value as T)) return value as T
  const raw = typeof value === 'string' ? value : ''
  return legacy?.[raw] ?? fallback
}

function defaultSections(): ProfileSection[] {
  return coreSections.map((type) => ({ id: type, type, title: defaultSectionTitles[type], enabled: true }))
}

function normalizeSections(value: unknown, legacyOrder: unknown): ProfileSection[] {
  if (Array.isArray(value) && value.length) {
    const out: ProfileSection[] = []
    for (const raw of value.slice(0, 10)) {
      const item = raw as Partial<ProfileSection>
      if (!item || !sectionTypes.includes(item.type as SectionType)) continue
      const type = item.type as SectionType
      const id = typeof item.id === 'string' && item.id.trim() ? item.id.trim().slice(0, 48) : `${type}-${out.length}`
      const title = typeof item.title === 'string' && item.title.trim() ? item.title.trim().slice(0, 60) : defaultSectionTitles[type]
      if (out.some((section) => section.id === id)) continue
      out.push({ id, type, title, enabled: item.enabled !== false })
    }
    if (out.length) return out
  }
  if (Array.isArray(legacyOrder) && legacyOrder.length) {
    const order = legacyOrder.filter((type): type is SectionType => sectionTypes.includes(type as SectionType))
    if (order.length) {
      const rest = coreSections.filter((type) => !order.includes(type))
      return [...order, ...rest].map((type) => ({ id: type, type, title: defaultSectionTitles[type], enabled: true }))
    }
  }
  return defaultSections()
}

function normalizeFonts(value: unknown): FontTheme {
  const raw = (value ?? {}) as Partial<FontTheme>
  const role = (id: unknown) => typeof id === 'string' && (id === 'default' || fontById(id)) ? id : 'default'
  return { name: role(raw.name), username: role(raw.username), bio: role(raw.bio), heading: role(raw.heading), body: role(raw.body) }
}

function normalizeCustomTheme(value: unknown): CustomTheme | undefined {
  const raw = (value ?? {}) as Partial<CustomTheme>
  const theme: CustomTheme = { bg: normalizeHex(raw.bg), surface: normalizeHex(raw.surface), text: normalizeHex(raw.text), accent: normalizeHex(raw.accent) }
  return customThemeValid(theme) ? theme : undefined
}

const bannerLayoutList: BannerLayout[] = ['carousel', 'stack']
const oldBannerLayouts: Record<string, BannerLayout> = { marquee: 'carousel', swipe: 'carousel', grid: 'carousel', stack: 'stack' }
const ctaCategoryList: CtaCategory[] = ['book', 'coffee', 'community']
const bannerRatioList: BannerRatio[] = ['16:9', '1:1', '4:5', '3:1']

function normalizeSectionConfig(value: unknown): Record<string, BannerConfig> {
  if (!value || typeof value !== 'object') return {}
  const out: Record<string, BannerConfig> = {}
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object' || typeof id !== 'string' || !id) continue
    const item = raw as Partial<BannerConfig>
    const config: BannerConfig = {
      layout: bannerLayoutList.includes(item.layout as BannerLayout) ? item.layout as BannerLayout : (oldBannerLayouts[String(item.layout)] ?? 'carousel'),
      ratio: bannerRatioList.includes(item.ratio as BannerRatio) ? item.ratio as BannerRatio : '16:9',
      height: typeof item.height === 'number' && Number.isFinite(item.height) ? Math.max(120, Math.min(420, Math.round(item.height))) : 180,
    }
    out[id.slice(0, 48)] = config
  }
  return out
}

function normalizeCtas(value: unknown): CtaConfig[] {
  if (!Array.isArray(value)) return []
  const out: CtaConfig[] = []
  for (const raw of value) {
    const item = (raw ?? {}) as Partial<CtaConfig>
    if (typeof item.category !== 'string' || !ctaCategoryList.includes(item.category as CtaCategory)) continue
    if (out.some((entry) => entry.category === item.category)) continue
    const url = typeof item.url === 'string' && item.url.startsWith('https://') ? item.url : ''
    const info = ctaById(item.category)
    const title = typeof item.title === 'string' && item.title.trim() ? item.title.trim().slice(0, 40) : (info?.defaultTitle || info?.name || item.category)
    const subtitle = typeof item.subtitle === 'string' && item.subtitle.trim() ? item.subtitle.trim().slice(0, 60) : (info?.defaultSubtitle ?? '')
    out.push({
      category: item.category as CtaCategory,
      title, subtitle, url,
      enabled: item.enabled === true && Boolean(url),
      ...(item.platform ? { platform: String(item.platform).slice(0, 20) } : {}),
      ...(typeof item.logoUrl === 'string' && item.logoUrl ? { logoUrl: item.logoUrl } : {}),
      ...(typeof item.logoPublicId === 'string' && item.logoPublicId ? { logoPublicId: item.logoPublicId } : {}),
      ...(typeof item.logoAssetId === 'string' && item.logoAssetId ? { logoAssetId: item.logoAssetId } : {}),
    })
  }
  return out
}

function normalizeMusic(value: unknown): ProfileMusic | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Partial<ProfileMusic>
  if (typeof raw.url !== 'string' || !raw.url.startsWith('https://')) return null
  return { url: raw.url, publicId: raw.publicId ?? '', assetId: raw.assetId ?? '', title: (raw.title ?? '').toString().slice(0, 60) || 'Profile music', duration: typeof raw.duration === 'number' && raw.duration > 0 ? Math.round(raw.duration) : 0 }
}

function normalizeSavedPalettes(value: unknown): SavedPalette[] {
  if (!Array.isArray(value)) return []
  const out: SavedPalette[] = []
  for (const raw of value.slice(0, 12)) {
    const item = (raw ?? {}) as Partial<SavedPalette>
    const palette: SavedPalette = {
      id: typeof item.id === 'string' && item.id.trim() ? item.id.trim().slice(0, 40) : `pal_${out.length}`,
      name: typeof item.name === 'string' && item.name.trim() ? item.name.trim().slice(0, 24) : `Palette ${out.length + 1}`,
      bg: normalizeHex(item.bg), surface: normalizeHex(item.surface), text: normalizeHex(item.text), accent: normalizeHex(item.accent),
    }
    if (customThemeValid(palette) && !out.some((entry) => entry.id === palette.id)) out.push(palette)
  }
  return out
}

export function normalizeProfile(value: Partial<PublicProfile>): PublicProfile {
  const now = new Date().toISOString()
  const customTheme = normalizeCustomTheme(value.customTheme)
  const colorThemeId = colorIds.includes(value.colorThemeId as ColorThemeId)
    ? value.colorThemeId === 'custom' && !customTheme ? 'emerald-night' : value.colorThemeId as ColorThemeId
    : 'emerald-night'
  const music = normalizeMusic(value.music)
  const normalized = {
    uid: value.uid ?? '',
    username: value.username ?? '',
    displayName: value.displayName || value.username || 'Linkme creator',
    bio: value.bio ?? '',
    photoUrl: value.photoUrl ?? '', photoPublicId: value.photoPublicId ?? '', photoAssetId: value.photoAssetId ?? '',
    backgroundUrl: value.backgroundUrl ?? '', backgroundPublicId: value.backgroundPublicId ?? '', backgroundAssetId: value.backgroundAssetId ?? '',
    backgroundFocusX: clamp(value.backgroundFocusX, 50), backgroundFocusY: clamp(value.backgroundFocusY, 50),
    backgroundContrast: pick<ContrastMode>(value.backgroundContrast, contrastList, 'strong'),
    profileDesignId: pick<ProfileDesignId>(value.profileDesignId ?? value.profileVariant, profileDesignIds, 'centered', oldProfileDesigns),
    colorThemeId,
    customTheme,
    avatarShape: pick<AvatarShape>(value.avatarShape, shapeList, 'circle'),
    avatarBorder: pick<BorderStyle>(value.avatarBorder, borderList, 'solid'),
    avatarBorderWidth: pick<BorderWidth>(value.avatarBorderWidth, widthList, 'thin'),
    avatarSize: pick<AvatarSize>(value.avatarSize, sizeList, 'm'),
    frameStyle: pick<FrameStyle>(value.frameStyle ?? value.borderStyle, frameList, 'none', oldFrameStyles),
    borderWidth: pick<BorderWidth>(value.borderWidth, widthList, 'regular'),
    borderColor: pick<BorderColorChoice>(value.borderColor, borderColorList, 'auto'),
    borderColorHex: normalizeHex(value.borderColorHex),
    buttonRadius: pick<ButtonRadius>(value.buttonRadius, radiusList, 'rounded'),
    buttonLook: pick<ButtonLook>(value.buttonLook, buttonLookList, 'surface'),
    buttonSize: pick<ButtonSize>(value.buttonSize, buttonSizeList, 'normal'),
    buttonIconShape: pick<IconShape>(value.buttonIconShape, iconShapeList, 'circle'),
    featuredStyle: pick<FeaturedStyle>(value.featuredStyle, featuredList, 'fill'),
    pressFeedback: pick<PressFeedback>(value.pressFeedback, pressList, 'scale'),
    surfaceOpacity: pick<SurfaceOpacity>(value.surfaceOpacity, surfaceOpacityList, 'high'),
    fontScale: pick<FontScale>(value.fontScale, fontScaleList, 'normal'),
    titleCase: pick<TitleCase>(value.titleCase, titleCaseList, 'uppercase'),
    titleAlign: pick<TitleAlign>(value.titleAlign, titleAlignList, 'left'),
    pageWidth: pick<PageWidth>(value.pageWidth, pageWidthList, 'normal'),
    sectionGap: pick<SectionGap>(value.sectionGap, sectionGapList, 'normal'),
    badgeIcon: pick<BadgeIcon>(value.badgeIcon, badgeIconList, 'check'),
    savedPalettes: normalizeSavedPalettes(value.savedPalettes),
    fonts: normalizeFonts(value.fonts),
    linkButtonStyle: pick<LinkButtonStyle>(value.linkButtonStyle, linkButtonList, 'full'),
    socialStyle: pick<SocialStyle>(value.socialStyle ?? (value as { socialDisplay?: string }).socialDisplay, socialList, 'logos', oldSocialStyles),
    contactStyle: pick<ContactStyle>(value.contactStyle, contactList, 'buttons'),
    projectsDisplay: pick<SectionDisplay>(value.projectsDisplay, displayList, 'swipe'),
    projectsCard: pick<CardStyle>(value.projectsCard, cardList, 'plain'),
    videosDisplay: pick<SectionDisplay>(value.videosDisplay, displayList, 'carousel'),
    videosCard: pick<CardStyle>(value.videosCard, cardList, 'plain'),
    sections: normalizeSections(value.sections, value.sectionOrder),
    music, musicEnabled: value.musicEnabled ?? true,
    ctas: normalizeCtas(value.ctas),
    bannersMigrated7: value.bannersMigrated7 ?? false,
    sectionConfig: normalizeSectionConfig(value.sectionConfig),
    badgePosition: pick<BadgePosition>(value.badgePosition, badgeList, 'avatar'),
    showUsername: value.showUsername ?? true,
    showBio: value.showBio ?? true,
    emailVerified: value.emailVerified ?? false,
    brandingVisible: value.brandingVisible ?? true,
    published: value.published ?? true,
    email: value.email ?? '', phone: value.phone ?? '', contactVisible: value.contactVisible ?? false,
    timezone: value.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    hasUnpublishedChanges: value.hasUnpublishedChanges ?? false,
    pendingMediaDeletes: Array.isArray(value.pendingMediaDeletes) ? value.pendingMediaDeletes : [],
    lastPublishedAt: value.lastPublishedAt,
    createdAt: value.createdAt ?? now, updatedAt: value.updatedAt ?? now,
  }
  // Never emit undefined values, Firestore rejects writes containing them.
  return stripUndefined(normalized)
}
